import { Component, ElementType, ReactNode, Suspense } from 'react';

// project imports
import Loader from './Loader';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: any;
}

class LazyErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: any): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: any) {
    const errorString = error?.toString() || '';
    const errorMessage = error?.message || '';

    if (
      /Failed to fetch dynamically imported module/i.test(errorMessage) ||
      /Failed to fetch dynamically imported module/i.test(errorString) ||
      /Importing a module script failed/i.test(errorMessage) ||
      /Importing a module script failed/i.test(errorString) ||
      /error loading dynamically imported module/i.test(errorMessage) ||
      /error loading dynamically imported module/i.test(errorString)
    ) {
      const hasRefreshed = sessionStorage.getItem('retry-lazy-refreshed');
      if (!hasRefreshed) {
        sessionStorage.setItem('retry-lazy-refreshed', 'true');
        window.location.reload();
      }
    }
  }

  render() {
    if (this.state.hasError) {
      const error = this.state.error;
      const errorString = error?.toString() || '';
      const errorMessage = error?.message || '';

      const isDynamicImportError =
        /Failed to fetch dynamically imported module/i.test(errorMessage) ||
        /Failed to fetch dynamically imported module/i.test(errorString) ||
        /Importing a module script failed/i.test(errorMessage) ||
        /Importing a module script failed/i.test(errorString) ||
        /error loading dynamically imported module/i.test(errorMessage) ||
        /error loading dynamically imported module/i.test(errorString);

      if (isDynamicImportError) {
        return <Loader />;
      }

      // Re-throw other errors so they bubble up to React Router's error boundaries
      throw this.state.error;
    }
    return this.props.children;
  }
}

// ==============================|| LOADABLE - LAZY LOADING ||============================== //

const Loadable = (Component: ElementType) => (props: any) => {
  return (
    <Suspense fallback={<Loader />}>
      <LazyErrorBoundary>
        <Component {...props} />
      </LazyErrorBoundary>
    </Suspense>
  );
};

export default Loadable;
